import { Capacitor } from "@capacitor/core";
import { isImage, processAttachment } from "@msys/ui";
import {
  AddAPhoto as AddAPhotoIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useFormikContext } from "formik";
import React from "react";
import { v4 } from "uuid";
import * as Yup from "yup";
import { Props2 } from "../../../../clients/graphqlTypes.js";
import { CheckboxField } from "../../../commons/form-fields/CheckboxField.js";
import { FormikError } from "../../../commons/form-fields/FormikError.js";
import {
  AttachmentPhotoUploaderWithEditor,
  AttachmentPhotoUploaderWithEditorRef,
} from "../../attachments/AttachmentPhotoUploaderWithEditor.js";
import { AttachmentUploader } from "../../attachments/AttachmentUploader.js";
import { Attachment, getRotatedUrl } from "../../attachments/helpers.js";
import { PropertyGalleryBox } from "../boxes/PropertyGalleryBox.js";
import { isBooleanType, isNonComputedProp } from "../properties.js";
import { usePropertyHelpers } from "../usePropertyHelpers.js";

export type ValueMediaBool = {
  key: string;
  value: boolean;
  media: Attachment | null;
};

export interface ValuesMediaBoolFormValues {
  defineValuesMediaBool: boolean;
  valuesMediaBool: ValueMediaBool[];
}

export function getValuesMediaBool(prop: Props2 | null): ValueMediaBool[] {
  return prop && isNonComputedProp(prop) && isBooleanType(prop)
    ? [
        {
          key: v4(),
          media:
            prop.valuesMediaBool.find(av => av.bool === true)?.media ?? null,
          value: true,
        },
        {
          key: v4(),
          media:
            prop.valuesMediaBool.find(av => av.bool === false)?.media ?? null,
          value: false,
        },
      ]
    : [
        {
          key: v4(),
          media: null,
          value: true,
        },
        {
          key: v4(),
          media: null,
          value: false,
        },
      ];
}

export function isSameTypeForValuesMediaBool(
  prop1: Props2,
  prop2: Props2
): boolean {
  return (
    isNonComputedProp(prop1) &&
    isNonComputedProp(prop2) &&
    isBooleanType(prop1) &&
    isBooleanType(prop2)
  );
}

interface Props {
  disabled?: boolean;
}

export const ValuesMediaBoolField = ({ disabled }: Props) => {
  const { t } = useTranslate(["Global", "QuoteItem"]);
  const formikProps = useFormikContext<ValuesMediaBoolFormValues>();

  return (
    <>
      <CheckboxField
        name={"defineValuesMediaBool"}
        label={t("Define values", {
          ns: "QuoteItem",
        })}
        disabled={disabled}
      />
      {formikProps.values.defineValuesMediaBool && (
        <Stack
          direction="column"
          alignSelf={"stretch"}
          spacing={1}
          paddingLeft={4}
        >
          <Stack direction="column" alignSelf={"stretch"} spacing={1}>
            {formikProps.values.valuesMediaBool.map((value, index) => (
              <ValueMediaBoolField
                name="valuesMediaBool"
                value={value}
                key={`value-${value.key}`}
                index={index}
                disabled={disabled}
              />
            ))}
          </Stack>
          <FormikError formikProps={formikProps} name="valuesMediaBool" />
        </Stack>
      )}
    </>
  );
};

function ValueMediaBoolField({
  name,
  value,
  index,
  disabled,
}: {
  name: string;
  value: ValueMediaBool;
  index: number;
  disabled?: boolean;
}) {
  const { t } = useTranslate(["QuoteItem", "Global"]);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const formikProps = useFormikContext<ValuesMediaBoolFormValues>();

  const { getBoolLabel } = usePropertyHelpers();

  const attachmentInputRef = React.useRef<HTMLInputElement>(null);
  const attachmentPhotoUploaderRef =
    React.useRef<AttachmentPhotoUploaderWithEditorRef>(null);

  return (
    <Stack
      direction="row"
      spacing={1}
      key={index}
      justifyContent="space-between"
      alignItems="center"
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexGrow={1}
        flexShrink={1}
      >
        {value.media && (
          <PropertyGalleryBox
            pictures={[value.media].map(processAttachment).filter(isImage)}
            flexGrow={0}
            flexShrink={0}
            objectFit="contain"
            width="80px"
            height="60px"
            imageHeight={60}
            aspectRatio="80 / 60"
            notInStack
            handleRotate={async (attachment, direction) => {
              await formikProps.setFieldValue(
                `${name}.${index}.media.url`,
                getRotatedUrl(attachment.url, direction)
              );
            }}
            handleDelete={async () => {
              await formikProps.setFieldValue(`${name}.${index}.media`, null);
            }}
          />
        )}
        <Typography>{getBoolLabel(value.value)}</Typography>
      </Stack>
      <Stack direction="row" spacing={0.5} alignItems="center">
        {!value.media ? (
          <>
            <IconButton
              size="small"
              color="secondary"
              disabled={disabled || isSubmitting}
              onClick={async () => {
                if (disabled) return;

                if (Capacitor.isNativePlatform()) {
                  attachmentPhotoUploaderRef.current?.start();
                } else {
                  attachmentInputRef.current?.click();
                }
              }}
            >
              {isSubmitting ? (
                <CircularProgress color="inherit" size={24} />
              ) : (
                <AddAPhotoIcon />
              )}
            </IconButton>
            {Capacitor.isNativePlatform() && (
              <AttachmentPhotoUploaderWithEditor
                ref={attachmentPhotoUploaderRef}
                onStart={() => {
                  setIsSubmitting(true);
                }}
                onAttachment={async attachment => {
                  if (attachment) {
                    await formikProps.setFieldValue(
                      `${name}.${index}.media`,
                      attachment
                    );
                  }
                  setIsSubmitting(false);
                }}
              />
            )}
            <AttachmentUploader
              innerRef={attachmentInputRef}
              accept={"image/*,.heic,.heif"}
              multiple={false}
              onStart={() => {
                setIsSubmitting(true);
              }}
              onComplete={async attachment => {
                if (attachment) {
                  await formikProps.setFieldValue(
                    `${name}.${index}.media`,
                    attachment
                  );
                }
                setIsSubmitting(false);
              }}
            />
          </>
        ) : (
          <IconButton
            size="small"
            color="primary"
            disabled={disabled}
            onClick={async () => {
              await formikProps.setFieldValue(`${name}.${index}.media`, null);
            }}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </Stack>
    </Stack>
  );
}

export const useValuesMediaBoolFieldValidationSchema = () => {
  const { t } = useTranslate(["QuoteItem", "Global"]);

  return React.useMemo(
    () => ({
      defineValuesMediaBool: Yup.boolean().required(),
      valuesMediaBool: Yup.array().of(
        Yup.object().shape({
          value: Yup.boolean()
            .nullable()
            .required()
            .label(t("Value", { ns: "Global" })),
          media: Yup.object().nullable(),
        })
      ),
    }),
    [t]
  );
};
